import { useState, useRef, useEffect, type FormEvent } from "react"
import { Link, router, usePage } from "@inertiajs/react"
import { Menu, X, Search, Music, User, LogOut, Heart, ChevronDown } from "lucide-react"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { VerificationBanner } from "@/components/VerificationBanner"
import { GuestQuizClaimBanner } from "@/components/GuestQuizClaimBanner"

const NAV_ITEMS = [
  { label: "Bands",      href: "/bands" },
  { label: "People",     href: "/people" },
  { label: "Recordings", href: "/recordings" },
  { label: "Labels",     href: "/record_labels" },
  { label: "Venues",     href: "/venues" },
  { label: "Trivia",     href: "/trivia" },
  { label: "About",      href: "/about" },
]

type CurrentUser = { id: number; name: string; email: string; admin: boolean; can_manage: boolean } | null

function UserMenu({ user, adFree }: { user: CurrentUser; adFree: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handle)
    return () => document.removeEventListener("mousedown", handle)
  }, [])

  if (!user) {
    return (
      <Link
        href="/login"
        className="px-3 py-1.5 text-sm font-medium text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-stone-800 rounded-md transition-colors no-underline"
      >
        Log in
      </Link>
    )
  }

  const initials = user.email.slice(0, 2).toUpperCase()

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 px-2 py-1.5 transition-colors"
        aria-label="User menu"
        aria-expanded={open}
      >
        <div
          className="w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 flex items-center justify-center text-xs font-semibold shrink-0"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {initials}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-stone-400 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-1.5 w-52 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl shadow-lg z-50 overflow-hidden py-1">
          <div className="px-4 py-2.5 border-b border-stone-100 dark:border-stone-800 mb-1">
            <p className="text-sm font-semibold text-stone-900 dark:text-white truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
              {user.email}
            </p>
          </div>
          <Link href="/profile" onClick={() => setOpen(false)} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors no-underline">
            <User className="w-4 h-4 text-stone-400 shrink-0" />
            My Profile
          </Link>
          {!adFree && (
            <Link href="/donate" onClick={() => setOpen(false)} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/50 transition-colors no-underline">
              <Heart className="w-4 h-4 shrink-0" />
              Donate
            </Link>
          )}
          <div className="border-t border-stone-100 dark:border-stone-800 my-1" />
          <button
            onClick={() => { router.delete("/logout"); setOpen(false) }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
          >
            <LogOut className="w-4 h-4 text-stone-400 shrink-0" />
            Log Out
          </button>
        </div>
      )}
    </div>
  )
}

export function PublicShell({ children }: { children: React.ReactNode }) {
  const { current_user, ad_free, ziggy } = usePage<{ current_user: CurrentUser; ad_free: boolean; ziggy?: { location?: string } }>().props
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)

  const currentPath = typeof window !== "undefined"
    ? window.location.pathname
    : (ziggy?.location ?? "")

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus()
  }, [searchOpen])

  function submitSearch(e: FormEvent) {
    e.preventDefault()
    const q = searchQuery.trim()
    if (!q) return
    setSearchOpen(false)
    router.get("/search", { q }, { preserveState: false })
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-6">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 shrink-0 no-underline">
              <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center shrink-0">
                <Music className="w-4 h-4 text-white" strokeWidth={2} />
              </div>
              <div className="hidden sm:block text-left leading-none" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                <span className="block text-sm font-semibold text-stone-900 dark:text-white">Australian</span>
                <span className="block text-xs font-normal text-stone-400 dark:text-stone-500 -mt-0.5">Music Database</span>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-0.5 flex-1">
              {NAV_ITEMS.map((item) => {
                const isActive = currentPath.startsWith(item.href)
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={[
                      "relative px-3 py-2 text-sm font-medium rounded-md transition-colors no-underline",
                      isActive
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-stone-800",
                    ].join(" ")}
                    style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                  >
                    {item.label}
                    {isActive && <span className="absolute bottom-0 inset-x-3 h-0.5 bg-amber-500 rounded-full" />}
                  </Link>
                )
              })}
              {current_user?.can_manage && (
                <Link
                  href="/admin"
                  className={[
                    "relative px-3 py-2 text-sm font-medium rounded-md transition-colors no-underline",
                    currentPath.startsWith("/admin")
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-stone-800",
                  ].join(" ")}
                  style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                >
                  Admin
                  {currentPath.startsWith("/admin") && <span className="absolute bottom-0 inset-x-3 h-0.5 bg-amber-500 rounded-full" />}
                </Link>
              )}
            </nav>

            {/* Right: support + search + theme + user + mobile */}
            <div className="flex items-center gap-1 ml-auto">
              {!ad_free && (
                <Link
                  href="/donate"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded-full transition-colors no-underline"
                >
                  <Heart className="w-3 h-3 fill-current" />
                  Support
                </Link>
              )}
              <button
                onClick={() => { setSearchOpen(!searchOpen); setMobileOpen(false) }}
                className="p-2 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-stone-800 rounded-md transition-colors"
                aria-label="Search"
              >
                <Search className="w-4 h-4" />
              </button>
              <ThemeToggle className="hidden sm:inline-flex" />
              <UserMenu user={current_user} adFree={ad_free} />
              <button
                onClick={() => { setMobileOpen(!mobileOpen); setSearchOpen(false) }}
                className="md:hidden p-2 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:bg-stone-100 dark:hover:bg-stone-800 rounded-md transition-colors"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
              >
                {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {searchOpen && (
            <div className="pb-3">
              <form onSubmit={submitSearch}>
                <input
                  ref={searchRef}
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search bands, people, recordings, venues…"
                  className="w-full px-4 py-2.5 bg-stone-100 dark:bg-stone-800 rounded-lg text-sm text-stone-900 dark:text-white placeholder-stone-400 dark:placeholder-stone-500 outline-none focus:ring-2 focus:ring-amber-500 border border-transparent focus:border-amber-300 dark:focus:border-amber-700"
                />
              </form>
            </div>
          )}
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
            <nav className="px-4 py-2 flex flex-col gap-0.5">
              {NAV_ITEMS.map((item) => {
                const isActive = currentPath.startsWith(item.href)
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={[
                      "text-left px-3 py-3 text-sm font-medium rounded-lg transition-colors no-underline",
                      isActive
                        ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50"
                        : "text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800",
                    ].join(" ")}
                  >
                    {item.label}
                  </Link>
                )
              })}
              {current_user?.can_manage && (
                <Link
                  href="/admin"
                  onClick={() => setMobileOpen(false)}
                  className={[
                    "text-left px-3 py-3 text-sm font-medium rounded-lg transition-colors no-underline",
                    currentPath.startsWith("/admin")
                      ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50"
                      : "text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800",
                  ].join(" ")}
                >
                  Admin
                </Link>
              )}
              {!ad_free && (
                <Link
                  href="/donate"
                  onClick={() => setMobileOpen(false)}
                  className="text-left px-3 py-3 text-sm font-medium rounded-lg transition-colors no-underline text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/50 flex items-center gap-2"
                >
                  <Heart className="w-4 h-4 fill-current shrink-0" />
                  Support the database
                </Link>
              )}
            </nav>
            <div className="px-4 py-3 border-t border-stone-100 dark:border-stone-800">
              <ThemeToggle block />
            </div>
          </div>
        )}
      </header>

      <VerificationBanner />
      <GuestQuizClaimBanner />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      <footer className="border-t border-stone-200 dark:border-stone-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-stone-400 dark:text-stone-500">© Australian Music Database</p>
          {/* Must wrap: six links in a single non-wrapping row overflowed a
              390px phone once Privacy and Terms were added (#342). */}
          <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 items-center">
            <Link href="/about" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
              About
            </Link>
            {ad_free ? (
              <Link href="/donate" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
                Donate
              </Link>
            ) : (
              <Link href="/donate" className="text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors no-underline inline-flex items-center gap-1">
                <Heart className="w-3 h-3 fill-current" />
                Donate
              </Link>
            )}
            <Link href="/wall-of-fame" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
              Wall of Fame
            </Link>
            <Link href="/leaderboard" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
              Leaderboard
            </Link>
            <Link href="/contact" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
              Contact
            </Link>
            <Link href="/privacy" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
              Privacy
            </Link>
            <Link href="/terms" className="text-sm text-stone-500 dark:text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline">
              Terms
            </Link>
          </nav>
        </div>
      </footer>
    </div>
  )
}
